import { useEffect, useRef, useState } from "react";

interface UsePullToRefreshParams {
  onRefresh: () => Promise<unknown> | unknown;
  threshold?: number;
  maxPullDistance?: number;
  resistance?: number;
  disabled?: boolean;
}

interface UsePullToRefreshResult<T extends HTMLElement> {
  scrollRef: React.RefObject<T>;
  pullDistance: number;
  isRefreshing: boolean;
  /** True while a finger is actively pulling; lets callers skip snap-back easing so the indicator tracks the finger. */
  isPulling: boolean;
}

const DEFAULT_THRESHOLD = 72;
const DEFAULT_MAX_PULL_DISTANCE = 96;
const DEFAULT_RESISTANCE = 2.5;

/**
 * Touch-driven pull-to-refresh for a scrollable element: tracks a downward drag
 * from the top and runs `onRefresh` once released past `threshold`.
 *
 * The move listener is non-passive so it can `preventDefault` the browser's
 * native overscroll while actively pulling.
 */
export function usePullToRefresh<T extends HTMLElement = HTMLDivElement>({
  onRefresh,
  threshold = DEFAULT_THRESHOLD,
  maxPullDistance = DEFAULT_MAX_PULL_DISTANCE,
  resistance = DEFAULT_RESISTANCE,
  disabled = false,
}: UsePullToRefreshParams): UsePullToRefreshResult<T> {
  const scrollRef = useRef<T>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);

  // Gesture bookkeeping kept in refs so touchmove doesn't re-render every frame.
  const gestureRef = useRef({ startY: 0, active: false, pull: 0 });
  const isRefreshingRef = useRef(false);
  // Hold the latest onRefresh so a new callback identity doesn't re-attach the
  // listeners on every render.
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    const element = scrollRef.current;
    if (!element || disabled) {
      return undefined;
    }

    function setPull(next: number) {
      gestureRef.current.pull = next;
      setPullDistance(next);
    }

    function handleTouchStart(event: TouchEvent) {
      if (isRefreshingRef.current || event.touches.length !== 1) {
        return;
      }

      if (element!.scrollTop <= 0) {
        gestureRef.current.active = true;
        gestureRef.current.startY = event.touches[0]!.clientY;
        setIsPulling(true);
      }
    }

    function handleTouchMove(event: TouchEvent) {
      if (!gestureRef.current.active || isRefreshingRef.current) {
        return;
      }

      const delta = event.touches[0]!.clientY - gestureRef.current.startY;
      // Not an at-top downward pull: let the browser scroll normally.
      if (element!.scrollTop > 0 || delta <= 0) {
        if (gestureRef.current.pull !== 0) {
          setPull(0);
        }

        return;
      }

      event.preventDefault();
      setPull(Math.min(delta / resistance, maxPullDistance));
    }

    function handleTouchEnd() {
      if (!gestureRef.current.active) {
        return;
      }

      gestureRef.current.active = false;
      // Released: re-enable snap-back easing for the settle to threshold or 0.
      setIsPulling(false);
      const shouldRefresh = gestureRef.current.pull >= threshold;
      if (!shouldRefresh || isRefreshingRef.current) {
        setPull(0);
        return;
      }

      isRefreshingRef.current = true;
      setIsRefreshing(true);
      // Hold the indicator at the threshold while the refresh runs.
      setPull(threshold);

      void Promise.resolve(onRefreshRef.current()).finally(() => {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
        setPull(0);
      });
    }

    element.addEventListener("touchstart", handleTouchStart, { passive: true });
    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchend", handleTouchEnd, { passive: true });
    element.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener("touchstart", handleTouchStart);
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchend", handleTouchEnd);
      element.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [disabled, maxPullDistance, resistance, threshold]);

  return { scrollRef, pullDistance, isRefreshing, isPulling };
}
